/**
 * Компонент 2: Map Engine
 * MapLibre GL JS с быстрым переключением подложек.
 * AIDEV-PROFESSIONAL: все растровые подложки добавлены заранее,
 * переключение только через visibility; overlay'и и слои лотов
 * хранятся отдельно от base style и не пересоздаются при смене подложки.
 */
(function() {
  'use strict';

  const maplibregl = window.maplibregl;

  const RUSSIA_BOUNDS = [19.0, 41.0, 190.0, 82.0];

  // OpenFreeMap vector styles (free, no API key, global coverage)
  const vectorStyleUrls = {
    'liberty':  'https://tiles.openfreemap.org/styles/liberty',
    'positron': 'https://tiles.openfreemap.org/styles/positron',
    'dark':     'https://tiles.openfreemap.org/styles/dark'
  };

  // AIDEV-NOTE: большинство растровых подложек теперь идут напрямую к
  // провайдерам, а не через /tiles/proxy. Это убирает лишний хоп до нашего
  // сервера и ServiceWorker для базовых тайлов, что даёт скорость ближе к
  // nakarte.me. Исключения: НСПД (нужен Referer/IP) и Bing (требуется quadkey,
  // оставлены на нашем прокси с Lua-преобразованием).
  // All raster base tiles go through our OpenResty proxy. This avoids CSP
  // connect-src issues (external tile hosts would need explicit, non-wildcard
  // entries) and lets the Service Worker / nginx tile cache accelerate loads.
  const rasterConfig = {
    '2gis':       { tiles: [window.location.origin + '/tiles/proxy/2gis/{z}/{x}/{y}'],       maxzoom: 18, attribution: '© 2GIS' },
    'yandex':     { tiles: [window.location.origin + '/tiles/proxy/yandex/{z}/{x}/{y}'],     maxzoom: 19, attribution: '© Yandex', bounds: RUSSIA_BOUNDS },
    'google':     { tiles: [window.location.origin + '/tiles/proxy/google/{z}/{x}/{y}'],     maxzoom: 21, attribution: '© Google' },
    'cartodb':    { tiles: [window.location.origin + '/tiles/proxy/cartodb/{z}/{x}/{y}.png'], maxzoom: 19, attribution: '© CartoDB' },
    'osm':        { tiles: [window.location.origin + '/tiles/proxy/osm/{z}/{x}/{y}.png'],     maxzoom: 19, attribution: '© OSM' },
    'yandex-sat': { tiles: [window.location.origin + '/tiles/proxy/yandex-sat/{z}/{x}/{y}'], maxzoom: 19, attribution: '© Yandex', bounds: RUSSIA_BOUNDS },
    'google-sat': { tiles: [window.location.origin + '/tiles/proxy/google-sat/{z}/{x}/{y}'], maxzoom: 21, attribution: '© Google' },
    'satellite':  { tiles: [window.location.origin + '/tiles/proxy/esri/{z}/{y}/{x}'],       maxzoom: 19, attribution: '© ESRI' },
    'nspd':       { tiles: [window.location.origin + '/nspd/wmts/235/{z}/{x}/{y}.png'],       maxzoom: 18, attribution: '© НСПД', bounds: RUSSIA_BOUNDS },
    'digimap':    { tiles: [window.location.origin + '/tiles/proxy/digimap/{z}/{x}/{y}.png'], maxzoom: 19, attribution: '© Digimap' },
    'waze':       { tiles: [window.location.origin + '/tiles/proxy/waze/{z}/{x}/{y}.png'],    maxzoom: 19, attribution: '© Waze' },
    'bing':       { tiles: [window.location.origin + '/tiles/proxy/bing/{z}/{x}/{y}'],        maxzoom: 19, attribution: '© Bing' },
    'bing-sat':   { tiles: [window.location.origin + '/tiles/proxy/bing-sat/{z}/{x}/{y}'],    maxzoom: 19, attribution: '© Bing' }
  };

  const overlaysConfig = {
    // AIDEV-NOTE: 36048/36049 в НСПД — WMS-слои, а не WMTS.
    // MapLibre поддерживает {bbox-epsg-3857} для растровых WMS-тайлов.
    'cadastre': { tiles: [window.location.origin + '/nspd/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.30, minzoom: 4, maxzoom: 19 },
    'nspd-zu':  { tiles: [window.location.origin + '/nspd/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.70, minzoom: 4, maxzoom: 19 },
    'nspd-oks': { tiles: [window.location.origin + '/nspd/wms/36049?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.70, minzoom: 4, maxzoom: 19 },
    // Map.ru cadastral layers (proxied and cached for 7 days)
    // Use 512 px tiles — map.ru renders them sharper than 256 px.
    'mapru-zu':  { tiles: [window.location.origin + '/mapru/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 },
    'mapru-oks': { tiles: [window.location.origin + '/mapru/wms/36049?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 },
    'mapru-ons': { tiles: [window.location.origin + '/mapru/wms/36329?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 }
  };

  let activeBaseLayer = 'osm';
  let initialLoadComplete = false;
  let vectorSwitchController = null;

  // ── Style helpers ──────────────────────────────────────────────

  async function loadStyle(url, externalSignal) {
    const controller = new AbortController();
    if (externalSignal) {
      externalSignal.addEventListener('abort', function() { controller.abort(); }, { once: true });
    }
    // Mobile/3G users need more time to fetch vector styles; 5 s was aborting
    // the request before MapLibre could finish, leaving the spinner forever.
    const timeout = setTimeout(function() { controller.abort(); }, 30000);
    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeout);
      if (!res.ok) throw new Error('Style load failed: ' + res.status);
      return await res.json();
    } catch (e) {
      clearTimeout(timeout);
      throw e;
    }
  }

  function buildRasterStyle(key) {
    // Delegate to the spec-validated style builder.
    if (window.__styleBuilder && typeof window.__styleBuilder.buildRasterStyle === 'function') {
      return window.__styleBuilder.buildRasterStyle(key);
    }
    // Fallback only if the style builder is somehow missing.
    const cfg = rasterConfig[key];
    const sourceId = 'base-' + key;
    const source = {
      type: 'raster',
      tiles: cfg.tiles,
      tileSize: 256,
      maxzoom: cfg.maxzoom,
      attribution: cfg.attribution || ''
    };
    if (cfg.bounds) source.bounds = cfg.bounds;
    return {
      version: 8,
      glyphs: window.location.origin + '/tiles/proxy/fonts/{fontstack}/{range}.pbf',
      sources: { [sourceId]: source },
      layers: [{
        id: sourceId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-fade-duration': 0,
          'raster-resampling': 'nearest'
        }
      }]
    };
  }

  // Add a single raster base source/layer (hidden by default)
  function addBaseRasterSource(map, key) {
    const layerId = 'base-' + key;
    if (map.getSource(layerId)) return;
    const cfg = rasterConfig[key];
    const source = {
      type: 'raster',
      tiles: cfg.tiles,
      tileSize: 256,
      maxzoom: cfg.maxzoom,
      attribution: cfg.attribution || ''
    };
    if (cfg.bounds) source.bounds = cfg.bounds;
    map.addSource(layerId, source);
    map.addLayer({
      id: layerId,
      type: 'raster',
      source: layerId,
      layout: { visibility: 'none' },
      paint: {
        'raster-fade-duration': 0,
        'raster-resampling': 'nearest'
      }
    });
  }

  // Lazy-load base source on first use. Pre-creating every source on startup
  // caused the browser to open dozens of tile connections and slowed initial
  // paint; nakarte.me keeps only the active tiles hot and fetches others on
  // demand, which we mirror here.
  function ensureBaseRasterSource(map, key) {
    const layerId = 'base-' + key;
    if (!map.getSource(layerId)) {
      addBaseRasterSource(map, key);
    }
    return layerId;
  }

  // Pre-add all raster base layers so switching is instant.
  // Used on initial load and after vector style changes.
  function addAllBaseRasterSources(map) {
    Object.keys(rasterConfig).forEach(function(key) {
      addBaseRasterSource(map, key);
    });
  }

  // Add overlay sources/layers once, on top of base, below lot layers
  function addOverlayLayers(map) {
    Object.entries(overlaysConfig).forEach(function([id, cfg]) {
      const layerId = 'overlay-' + id;
      if (map.getSource(layerId)) return;
      map.addSource(layerId, {
        type: 'raster',
        tiles: cfg.tiles,
        tileSize: cfg.tileSize || 256,
        minzoom: cfg.minzoom,
        maxzoom: cfg.maxzoom
      });
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: layerId,
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': cfg.opacity,
          'raster-fade-duration': 0,
          'raster-resampling': 'nearest'
        }
      });
    });
  }

  // Ensure persistent layers exist after a vector style change
  function restorePersistentLayers(map) {
    addOverlayLayers(map);
    // Restore all base raster sources after setStyle so switching stays instant.
    addAllBaseRasterSources(map);
    applyRasterBaseLayer(map, activeBaseLayer);
    // Move overlay layers below lot layers if lot layers already exist
    ['lots-circle', 'lots-polygons-fill', 'lots-polygons-line'].forEach(function(lotLayerId) {
      if (map.getLayer(lotLayerId)) {
        Object.keys(overlaysConfig).forEach(function(overlayKey) {
          const overlayId = 'overlay-' + overlayKey;
          if (map.getLayer(overlayId)) {
            try { map.moveLayer(overlayId, lotLayerId); } catch (e) {}
          }
        });
      }
    });
  }

  function applyRasterBaseLayer(map, layerKey, previousKey) {
    // Lazy-create the target source on first switch; hide the previous one.
    // This avoids paying the cost of 15+ unused tile sources at startup.
    if (previousKey && previousKey !== layerKey) {
      const prevId = 'base-' + previousKey;
      if (map.getLayer(prevId)) {
        map.setLayoutProperty(prevId, 'visibility', 'none');
      }
    }
    const targetId = ensureBaseRasterSource(map, layerKey);
    if (map.getLayer(targetId)) {
      map.setLayoutProperty(targetId, 'visibility', 'visible');
    }
  }

  function applyVectorBaseLayer(map, layerKey) {
    const vectorUrl = vectorStyleUrls[layerKey];
    if (!vectorUrl) return Promise.reject(new Error('Unknown vector layer: ' + layerKey));

    // Cancel an in-flight vector style fetch when the user picks another layer.
    if (vectorSwitchController) {
      try { vectorSwitchController.abort(); } catch (e) {}
    }
    const controller = new AbortController();
    vectorSwitchController = controller;

    return loadStyle(vectorUrl, controller.signal).then(function(style) {
      // If the user already switched to another layer while we were fetching,
      // discard this stale style instead of overwriting the map.
      if (controller.signal.aborted || activeBaseLayer !== layerKey) return;

      // Validate downloaded vector style against the MapLibre Style Spec.
      if (window.__styleBuilder && typeof window.__styleBuilder.validateStyle === 'function') {
        const validationErrors = window.__styleBuilder.validateStyle(style);
        if (validationErrors && validationErrors.length) {
          console.warn('[Map Engine] vector style validation warnings:', validationErrors);
        }
      }
      // Do not embed overlays into the vector style; they will be restored after style load.
      // diff: true lets MapLibre apply only the deltas instead of rebuilding the whole style.
      map.setStyle(style, { diff: true });
    }).finally(function() {
      if (vectorSwitchController === controller) vectorSwitchController = null;
    });
  }

  function applyBaseLayer(map, layerKey) {
    return new Promise(function(resolve, reject) {
      if (layerKey === activeBaseLayer) {
        resolve();
        return;
      }
      const previousKey = activeBaseLayer;
      const isVector = !!vectorStyleUrls[layerKey];

      if (isVector) {
        applyVectorBaseLayer(map, layerKey).then(function() {
          const onStyleData = function() {
            // Only restore overlays and announce completion for the latest request.
            if (activeBaseLayer !== layerKey) return;
            restorePersistentLayers(map);
            window.dispatchEvent(new CustomEvent('map:styleload', { detail: { map } }));
            resolve();
          };
          map.once('styledata', onStyleData);
        }).catch(function(err) {
          if (err && (err.name === 'AbortError' || (err.message && err.message.includes('aborted')))) {
            console.log('[Map Engine] Vector switch aborted by newer switch');
            resolve();
            return;
          }
          console.warn('[Map Engine] Vector style failed, falling back to OSM raster:', err);
          activeBaseLayer = 'osm';
          applyRasterBaseLayer(map, 'osm', previousKey);
          resolve();
        });
      } else {
        if (!rasterConfig[layerKey]) {
          reject(new Error('Unknown base layer: ' + layerKey));
          return;
        }
        // Instant raster switch: just toggle visibility
        activeBaseLayer = layerKey;
        applyRasterBaseLayer(map, layerKey, previousKey);
        resolve();
      }
    });
  }

  // ── Map init ───────────────────────────────────────────────────

  function initMap() {
    if (typeof maplibregl === 'undefined') {
      console.error('[Map Engine] maplibregl is not loaded');
      const ml = document.getElementById('map-loader');
      if (ml) ml.innerHTML = '<div style="color:#dc2626;text-align:center;padding:20px;">Библиотека карты не загрузилась.<br>Проверьте подключение к интернету.</div>';
      return;
    }

    const mapContainer = document.getElementById('map1');
    if (!mapContainer) {
      console.log('[Map Engine] map1 container not found, skipping map init');
      return;
    }

    // Guard: WebGL недоступен (старые устройства/строгие настройки) —
    // иначе maplibre падает с webglcontextcreationerror и карта пустая (35 ошибок/сутки)
    function webglOk() {
      try {
        const c = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
      } catch (e) { return false; }
    }
    if (!webglOk()) {
      console.error('[Map Engine] WebGL unavailable');
      const ml2 = document.getElementById('map-loader');
      if (ml2) ml2.innerHTML =
        '<div style="color:#333;text-align:center;padding:28px 20px;background:#fff;border-radius:12px;max-width:420px;margin:40vh auto 0;box-shadow:0 4px 20px rgba(0,0,0,.1)">'+
        '<div style="font-size:40px;margin-bottom:10px">🗺️</div>'+
        '<b>Не удалось включить карту</b><br>'+
        '<span style="font-size:13px;color:#666">Ваш браузер не поддерживает WebGL. '+
        'Обновите браузер или включите аппаратное ускорение в настройках.</span></div>';
      return;
    }

    const map = new maplibregl.Map({
      container: 'map1',
      style: buildRasterStyle(activeBaseLayer),
      center: [95, 65],
      zoom: 3,
      minZoom: 2,
      maxZoom: 19,
      attributionControl: false,
      preserveDrawingBuffer: false,
      localIdeographFontFamily: '"Noto Sans", "Open Sans", "Segoe UI", sans-serif',
      antialias: false,
      fadeDuration: 0,
      // Limit memory cache: we regenerate tiles quickly, no need to keep many zooms.
      maxTileCacheZoomLevels: 5,
      maxTileCacheSizeBytes: 20 * 1024 * 1024,
      // Skip tile revalidation; tiles are versioned by data, not by headers.
      refreshExpiredTiles: false,
      // Disable cross-source collision detection — major CPU save with our overlays.
      crossSourceCollisions: false,
      renderWorldCopies: false,
      projection: { type: 'mercator' },
      dragRotate: false,
      touchPitch: false,
      // Near-zero inertia so the map stops rendering animation frames and shows tiles immediately.
      dragPan: { inertia: 0, maxSpeed: Infinity, linearity: 1 },
      // Instant scroll zoom around the cursor (not the screen center) so the
      // point under the mouse stays fixed while zooming in/out.
      scrollZoom: { duration: 0 },
      touchZoomRotate: { pinchRotate: false, duration: 0 },
      // Keyboard shortcuts are unused and can steal scroll focus.
      keyboard: false
    });

    window.map = map;

    function hideLoader() {
      const ml = document.getElementById('map-loader');
      if (ml) ml.classList.add('hidden');
    }

    // Safety net: if MapLibre never fires 'load' (aborted style/tilejson
    // request, slow connection, etc.), do not leave the spinner forever.
    const loaderFallback = setTimeout(function() {
      console.warn('[Map Engine] Loader fallback triggered: hiding spinner despite no load event');
      hideLoader();
    }, 15000);

    map.on('load', function() {
      console.log('[Map Engine] map load event fired');
      clearTimeout(loaderFallback);

      // Pre-add all raster base sources and overlays so switching is instant.
      // (Lazy loading was suspected of causing blank base layers, restored
      // to the original eager strategy to keep the map visible immediately.)
      addAllBaseRasterSources(map);
      addOverlayLayers(map);

      // Make sure the initial active layer is visible
      applyRasterBaseLayer(map, activeBaseLayer);

      hideLoader();

      initialLoadComplete = true;
      window.__mapInitialLoadComplete = true;
      window.dispatchEvent(new CustomEvent('map:ready', { detail: { map } }));
    });

    let moveendTimeout = null;
    map.on('moveend', function() {
      clearTimeout(moveendTimeout);
      moveendTimeout = setTimeout(function() {
        window.dispatchEvent(new CustomEvent('map:moveend', {
          detail: {
            center: map.getCenter(),
            zoom: map.getZoom(),
            bounds: map.getBounds()
          }
        }));
      }, 200);
    });

    // Enforce north-up even if an external interaction/script sets bearing
    map.on('rotate', function() { if (map.getBearing() !== 0) map.setBearing(0); });
    map.on('rotateend', function() { if (map.getBearing() !== 0) map.setBearing(0); });

    map.on('error', function(e) {
      console.error('[Map Engine] Map error:', e);
      // Do not let an aborted request keep the loading spinner forever.
      clearTimeout(loaderFallback);
      hideLoader();
      window.dispatchEvent(new CustomEvent('map:error', {
        detail: { sourceId: e.source ? e.source.id : null, error: e.error ? e.error.message : String(e) }
      }));
    });

    // Mouse coordinates display + right-click copy
    const mousePosEl = document.getElementById('mouse-position');
    if (mousePosEl) {
      map.on('mousemove', function(e) {
        if (window._mapToolsSearchMode) return;
        mousePosEl.textContent = e.lngLat.lat.toFixed(6) + ', ' + e.lngLat.lng.toFixed(6);
      });

      map.on('contextmenu', function(e) {
        e.preventDefault();
        const text = e.lngLat.lat.toFixed(6) + ', ' + e.lngLat.lng.toFixed(6);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).catch(function() {});
        }
        const original = mousePosEl.textContent;
        mousePosEl.textContent = 'Скопировано: ' + text;
        setTimeout(function() { mousePosEl.textContent = original; }, 1200);
      });
    }
  }

  // ── Event listeners ────────────────────────────────────────────

  window.addEventListener('layer:switch', function(e) {
    const target = e.detail && e.detail.layerId;
    if (!target) return;
    const map = window.map;
    // Block only until the very first map load is complete. After that we allow
    // switching even while tiles are still loading — otherwise rapid clicks feel
    // frozen because map.loaded() becomes false while the previous base layer
    // fetches tiles.
    if (!map || !initialLoadComplete) return;
    applyBaseLayer(map, target).catch(function(err) {
      console.error('[Map Engine] Layer switch failed:', err);
    });
  });

  window.addEventListener('overlay:toggle', function(e) {
    const detail = e.detail;
    if (!detail || !detail.overlayId) return;
    const map = window.map;
    if (!map) return;
    const layerId = 'overlay-' + detail.overlayId;
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', detail.visible ? 'visible' : 'none');
    }
  });

  window.addEventListener('ui:geolocate', function() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(function(pos) {
      const map = window.map;
      if (map) map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 14, duration: 800 });
    }, function(err) {
      console.warn('[Map Engine] Geolocation error:', err);
    });
  });

  window.addEventListener('3d:toggle', function(e) {
    const map = window.map;
    if (!map) return;
    const enabled = e.detail && e.detail.enabled;
    if (enabled) {
      if (!map.getSource('terrain')) {
        try {
          map.addSource('terrain', {
            type: 'raster-dem',
            tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
            tileSize: 256,
            encoding: 'terrarium',
            maxzoom: 15,
            attribution: 'Terrain © Mapzen'
          });
        } catch (terrErr) {
          console.warn('[Map Engine] Terrain source failed:', terrErr);
        }
      }
      if (map.getSource('terrain')) {
        try { map.setTerrain({ source: 'terrain', exaggeration: 1.2 }); } catch (err) {}
      }
      map.easeTo({ pitch: 55, bearing: 0, duration: 1200 });
    } else {
      map.easeTo({ pitch: 0, bearing: 0, duration: 1200 });
      try { map.setTerrain(null); } catch (err) {}
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMap);
  } else {
    initMap();
  }
})();
